import { logger } from '../../datadog/logger';
import { normalizeError } from '../../utils';
import type { VideoClientEvent, VideoClientEventHandler } from './VideoClientEvents';
import type { PeerShareStateChangePayload } from './room/participant/predicates';
import { VideoClient } from './types';

/**
 * Singleton EventBus that intermediates Zoom VideoClient event broadcasting.
 *
 * Zoom uses a monolithic event emitter where all events are emitted through a single VideoClient
 * instance, unlike Twilio's granular approach with separate emitters per resource. This is a
 * pre-optimization based on performance and auditability issues surfaced during the technical spike.
 * For example, listening Twilio's RemoteAudio subscribed/unsubscribed/enabled events requires three
 * separate listeners on Zoom's single 'user-updated' event, and Twilio's Room
 * connected/reconnected/disconnected events are all handled by Zoom's single 'connection-changed' event.
 *
 * This EventBus implements a fan-out pattern that registers only one listener per Zoom event type
 * with the VideoClient and broadcasts to all handlers registered by our application code. This
 * addresses two important technical requirements: integrating Zoom's monolithic event listeners
 * in their recommended way while preserving our application's existing event listening paradigm.
 *
 * - Avoids MaxListenersExceededWarning through minimal listener registration
 * - Maintains existing application event handling patterns
 * - Enables future refactoring without breaking existing code
 */
export class ZoomEventBus {
  static #instance: ZoomEventBus | null = null;
  #videoClient: VideoClient;

  // register at most one event listener per Zoom event
  #zoomEventListeners = new Map<VideoClientEvent, VideoClientEventHandler<VideoClientEvent>>();

  // each registered event listener has list of handlers from application; Set preserves insertion order
  #applicationHandlers = new Map<VideoClientEvent, Set<VideoClientEventHandler<VideoClientEvent>>>();

  // track active screen share to workaround Zoom bug on non-Chrome browsers
  #activeShareUserIds = new Set<number>();

  private constructor(videoClient: VideoClient) {
    this.#videoClient = videoClient;
    this.#trackActiveShare();
    this.#logMediaFailures();
  }

  static initialize(videoClient: VideoClient): void {
    if (this.#instance) {
      logger.debug('ZoomEventBus is already initialized');
      return;
    }
    this.#instance = new ZoomEventBus(videoClient);
  }

  static getInstance(): ZoomEventBus {
    if (!this.#instance) {
      throw new Error('ZoomEventBus must be initialized first with initialize()');
    }

    return this.#instance;
  }

  static tearDown(): void {
    if (this.#instance) {
      // unregister all bound handlers from VideoClient
      for (const [event, zoomEventListener] of this.#instance.#zoomEventListeners) {
        this.#instance.#videoClient.off(event, zoomEventListener);
      }

      // clear all internal state
      this.#instance.#applicationHandlers.clear();
      this.#instance.#zoomEventListeners.clear();
      this.#instance.#activeShareUserIds.clear();

      this.#instance = null;
    }
  }

  isUserActivelySharing(userId: number): boolean {
    return this.#activeShareUserIds.has(userId);
  }

  on<T extends VideoClientEvent>(event: T, handler: VideoClientEventHandler<T>) {
    let handlersForEvent = this.#applicationHandlers.get(event);

    if (!handlersForEvent) {
      handlersForEvent = new Set();
      this.#applicationHandlers.set(event, handlersForEvent);

      // create closure that fans out to all current application handlers for this event
      const fanOutHandler = (...args: unknown[]) => {
        const handlersForEvent = this.#applicationHandlers.get(event);
        logger.debug('[ZoomEventBus] emitted event', {
          event,
          handlersInvoked: handlersForEvent?.size,
          args,
        });
        if (handlersForEvent) {
          for (const handler of handlersForEvent) {
            try {
              (handler as (...args: unknown[]) => void)(...args);
            } catch (error) {
              logger.error('ZoomEventBus encountered an unhandled exception', { event, args }, normalizeError(error));
            }
          }
        }
      };

      this.#zoomEventListeners.set(event, fanOutHandler);
      this.#videoClient.on(event, fanOutHandler);
    }

    handlersForEvent.add(handler);
  }

  off<T extends VideoClientEvent>(event: T, handler: VideoClientEventHandler<T>) {
    const handlersForEvent = this.#applicationHandlers.get(event);

    if (handlersForEvent) {
      handlersForEvent.delete(handler);

      // if no more application handlers, unregister from Zoom
      if (handlersForEvent.size === 0) {
        this.#applicationHandlers.delete(event);

        const zoomEventListener = this.#zoomEventListeners.get(event);
        if (zoomEventListener) {
          this.#videoClient.off(event, zoomEventListener);
          this.#zoomEventListeners.delete(event);
        }
      }
    }
  }

  #trackActiveShare() {
    const tracker = (payload: PeerShareStateChangePayload) => {
      if (payload.action === 'Start') {
        this.#activeShareUserIds.add(payload.userId);
      } else if (payload.action === 'Stop') {
        this.#activeShareUserIds.delete(payload.userId);
      }
    };
    this.on('peer-share-state-change', tracker);
  }

  #logMediaFailures() {
    this.on('active-media-failed', (payload) => {
      logger.error('[ZoomEventBus] active media failed', { ...payload });
    });
    this.on('auto-play-audio-failed', () => {
      logger.error('[ZoomEventBus] auto-play audio failed');
    });
  }
}
